/* List overrides: a table (List, AI regime, Hot transition, Call chatter,
   Interrupts, Sales). Anything left at the account default reads a muted
   "Default". A row expands in place to edit it. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OfList } from '../../../../api/client';
import { buildRegimeSelectOptions, regimeUsesCompletionChatter } from '../../aiRegimeOptions';
import { FOLDER_INTERRUPT_OVERRIDE_FIELDS, allInterruptsYesRecord } from '../../folderInterruptOverrides';
import { Btn, Card, Dropdown, Empty, Icon, Row, Seg, TextArea, Toggle } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { HOT_MODES, type HotMode, type Override, type ReqMode } from './model';
import { Box } from './parts';
import { Spinner } from '../../../Spinner';

const COLS = 'minmax(0,1.3fr) minmax(0,1.1fr) minmax(0,1fr) 90px 110px 110px 64px';
const N_INT = FOLDER_INTERRUPT_OVERRIDE_FIELDS.length;

export function Overrides({ rows, lists, loading, onChange }: {
  rows: Override[];
  lists: OfList[];
  loading: boolean;
  /** `now` = a choice (saves straight away); typing is debounced. */
  onChange: (rows: Override[], now?: boolean) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const [open, setOpen] = useState<string | null>(null);
  const name = (uuid: string) => lists.find((l) => l.list_uuid === uuid)?.of_list_name || uuid;
  const upd = (id: string, p: Partial<Override>, now = true) => onChange(rows.map((o) => (o.id === id ? { ...o, ...p } : o)), now);
  const hotLabel = (h: HotMode) => tc(`setup.hotOptions.${h}`);

  const add = () => {
    const used = new Set(rows.map((o) => o.list));
    const free = lists.find((l) => !used.has(l.list_uuid));
    const row: Override = { id: 'new-' + Date.now(), list: free?.list_uuid || '', regime: 'talk_and_tease', chatter: true, hot: 'moderate', prompt: '', ints: allInterruptsYesRecord(), content: null, custom: null, call: null, ppv: null };
    onChange([...rows, row], true);
    setOpen(row.id);
  };

  const modes = (decline: boolean) => [{ id: 'd', label: t('su.default') }, { id: 'handoff', label: t('su.handoff') }, { id: 'automate', label: t('su.automate') }, ...(decline ? [{ id: 'decline', label: t('su.decline') }] : [])];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: f(600, 13), color: C.text }}>{t('su.overrides')}</span>
        <span style={{ font: f(600, 10.5), color: C.purplePale, background: C.tint16, padding: '1px 6px', borderRadius: 99 }}>{t('su.nActive', { count: rows.length })}</span>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{t('su.overridesHint')}</span>
        <span style={{ flex: 1 }} />
        <Btn kind="text" icon="plus" height={26} onClick={add} disabled={loading} style={{ color: C.purpleLt }}>{t('su.addOverride')}</Btn>
      </div>
      <Card clip={false}>
        <div className="cpt-scroll-x">
          <div style={{ minWidth: 720 }}>
            <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', height: 30, padding: '0 14px', font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
              <span>{t('su.colList')}</span><span>{t('su.aiRegime')}</span><span>{t('su.hot')}</span><span>{t('su.callChatter')}</span><span>{t('su.interrupts')}</span><span>{t('su.salesShort')}</span><span />
            </div>
            {rows.map((o) => {
              const isOpen = open === o.id;
              const noChatter = !regimeUsesCompletionChatter(o.regime);
              const nOn = Object.values(o.ints).filter(Boolean).length;
              const nS = [o.ppv !== null, !!o.content, !!o.custom, !!o.call].filter(Boolean).length;
              const regLabel = buildRegimeSelectOptions(o.regime).find((r) => r.value === o.regime)?.label ?? o.regime;
              const used = new Set(rows.filter((x) => x.id !== o.id).map((x) => x.list));
              const listOpts = [...(o.list ? [] : [{ id: '', label: t('su.chooseList') }]), ...lists.filter((l) => !used.has(l.list_uuid)).map((l) => ({ id: l.list_uuid, label: l.of_list_name || l.list_uuid }))];
              if (o.list && !listOpts.some((x) => x.id === o.list)) listOpts.unshift({ id: o.list, label: name(o.list) });
              return (
                <div key={o.id} style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.divider}` }}>
                  <div role="button" tabIndex={0} onClick={() => setOpen(isOpen ? null : o.id)} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(isOpen ? null : o.id); }} className="cpt-hover-row" style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', height: 42, padding: '0 14px', cursor: 'pointer', font: f(500, 12.5), color: C.text }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Icon name="list" size={13} color={C.purpleLt} style={{ flex: 'none' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: o.list ? C.text : C.purplePale }}>{o.list ? name(o.list) : t('su.chooseList')}</span>
                    </span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{regLabel}</span>
                    <span>{hotLabel(o.hot)}</span>
                    <span style={{ color: noChatter ? C.muted : C.text }}>{noChatter ? '—' : o.chatter ? t('su.on') : t('su.off')}</span>
                    <span style={{ color: nOn === N_INT ? C.muted : C.text, whiteSpace: 'nowrap' }}>{nOn === N_INT ? t('su.allOn') : t('su.nOfOn', { n: nOn, total: N_INT })}</span>
                    <span style={{ color: nS ? C.text : C.muted, whiteSpace: 'nowrap' }}>{nS ? t('su.nChanged', { count: nS }) : t('su.default')}</span>
                    <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <button type="button" title={t('su.removeOverride')} onClick={(e) => { e.stopPropagation(); onChange(rows.filter((x) => x.id !== o.id), true); if (isOpen) setOpen(null); }} className="cpt-hover-text" style={{ width: 24, height: 24, border: 0, borderRadius: 5, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="trash-2" size={13} />
                      </button>
                      <span style={{ width: 24, height: 24, color: C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                        <Icon name="chevron-down" size={14} />
                      </span>
                    </span>
                  </div>
                  {isOpen && (
                    <div className="cpt-card" style={{ display: 'flex', flexDirection: 'column', background: 'var(--om-panel)', borderTop: `1px solid ${C.divider}`, paddingLeft: 24 }}>
                      <Row label={t('su.colList')}>
                        <Dropdown value={o.list} options={listOpts} onChange={(v) => upd(o.id, { list: v })} width={200} />
                      </Row>
                      <Row label={t('su.aiRegime')}>
                        <Dropdown value={o.regime} options={buildRegimeSelectOptions(o.regime).map((r) => ({ id: r.value, label: r.label, sub: r.description }))} onChange={(v) => upd(o.id, { regime: v })} width={200} menuWidth={320} />
                      </Row>
                      <div>
                        <Row label={t('su.hot')}>
                          <Seg options={HOT_MODES.map((h) => ({ id: h, label: hotLabel(h) }))} value={o.hot} onChange={(v) => upd(o.id, { hot: v })} />
                        </Row>
                        {o.hot === 'custom' && (
                          <div style={{ padding: '0 14px 10px' }}>
                            <TextArea value={o.prompt} onChange={(v) => upd(o.id, { prompt: v }, false)} placeholder={tc('setup.ovCustomPromptPlaceholder', { folder: name(o.list) })} height={72} />
                          </div>
                        )}
                      </div>
                      {!noChatter && (
                        <Row label={t('su.callChatter')} help={t('su.chatterHelp')}>
                          <Toggle on={o.chatter} onChange={(v) => upd(o.id, { chatter: v })} />
                        </Row>
                      )}
                      <div>
                        <Row label={t('su.interrupts')} help={t('su.intHelp')}>
                          <span style={{ font: f(500, 12), color: C.sub }}>{nOn === N_INT ? t('su.allOn') : t('su.nOfOn', { n: nOn, total: N_INT })}</span>
                          <Btn kind="text" height={26} onClick={() => upd(o.id, { ints: Object.fromEntries(Object.keys(o.ints).map((k) => [k, nOn !== N_INT])) as Override['ints'] })} style={{ color: C.purpleLt }}>
                            {nOn === N_INT ? t('su.turnAllOff') : t('su.turnAllOn')}
                          </Btn>
                        </Row>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '2px 12px', padding: '0 14px 10px' }}>
                          {FOLDER_INTERRUPT_OVERRIDE_FIELDS.map(({ key, label }) => {
                            const v = o.ints[key];
                            return (
                              <div key={key} role="button" tabIndex={0} onClick={() => upd(o.id, { ints: { ...o.ints, [key]: !v } })} onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); upd(o.id, { ints: { ...o.ints, [key]: !v } }); } }} className="cpt-hover-row" style={{ height: 28, padding: '0 6px', borderRadius: 5, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', font: f(400, 12.5), color: v ? 'var(--om-text-2)' : C.muted }}>
                                <Box on={v} />
                                <span title={label} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 14px 6px' }}>
                          <span style={{ font: f(500, 13), color: C.text }}>{t('su.sales')}</span>
                          <span style={{ font: f(400, 11.5), color: C.sub }}>{t('su.ovSalesHelp')}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', padding: '0 14px 10px' }}>
                          <Sale label={tc('setup.ppvOffers')} opts={[{ id: 'd', label: t('su.default') }, { id: 'on', label: t('su.on') }, { id: 'off', label: t('su.off') }]} value={o.ppv === null ? 'd' : o.ppv ? 'on' : 'off'} onChange={(v) => upd(o.id, { ppv: v === 'd' ? null : v === 'on' })} />
                          <Sale label={tc('setup.contentRequestsMode')} opts={modes(false)} value={o.content ?? 'd'} onChange={(v) => upd(o.id, { content: v === 'd' ? null : (v as 'handoff' | 'automate') })} />
                          <Sale label={tc('setup.customRequestsMode')} opts={modes(true)} value={o.custom ?? 'd'} onChange={(v) => upd(o.id, { custom: v === 'd' ? null : (v as ReqMode) })} />
                          <Sale label={tc('setup.callRequestsMode')} opts={modes(true)} value={o.call ?? 'd'} onChange={(v) => upd(o.id, { call: v === 'd' ? null : (v as ReqMode) })} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {!rows.length && <div style={{ borderTop: `1px solid ${C.divider}` }}><Empty pad={14}>{loading ? <Spinner size={16} label={t('loading')} /> : t('su.noOverrides')}</Empty></div>}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Sale({ label, opts, value, onChange }: { label: string; opts: { id: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 34 }}>
      <span style={{ font: f(400, 12.5), color: 'var(--om-body)', whiteSpace: 'nowrap' }}>{label}</span>
      <Seg options={opts} value={value} onChange={onChange} />
    </div>
  );
}
